import React, { useState, useEffect } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import type { OnboardingEditorState } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useCheckIfOnboardingEditorTemplate } from '@confluence/onboarding-helpers/entry-points/hooks/useCheckIfOnboardingEditorTemplate';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	ONBOARDING_EDITOR_DRAFT_BANNER_EXPERIENCE,
	ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { useIncludeEditorTutorialTemplate } from '@confluence/template-list/entry-points/hooks/useIncludeEditorTutorialTemplate';
import { useSessionData } from '@confluence/session-data';
import { ErrorDisplay, TransparentErrorBoundary, Attribution } from '@confluence/error-boundary';
import type { FlagsStateContainer } from '@confluence/flags';

import { EditorDraftBanner } from './EditorDraftBanner';
import { LandInEditorBanner } from './LandInEditorBanner';

type VersionType = { number?: number };

export type EditorBannerContainerProps = {
	isContentCreationPanelOpen: boolean;
	version?: number | VersionType;
	contentId: string;
	isShowingOfflineBanner: boolean;
	flags?: FlagsStateContainer;
	spaceKey: string;
	setIsOnboardingBannerDataLoaded: (value: boolean) => void;
	onClickOpenContentCreationPanel: () => void;
	editorActions: EditorActions;
	handleTitleChange: (newTitle: string) => void;
};

export type EditorBannerProps = {
	deserializedData: OnboardingEditorState | undefined;
	showingOfflineBanner: boolean;
	setIsVisible: (value: boolean) => void;
};

const DELAY_TIMEOUT = 3000;

export const BaseOnboardingEditorBanner = (props: EditorBannerContainerProps) => {
	const {
		contentId,
		version,
		isShowingOfflineBanner,
		spaceKey,
		editorActions,
		setIsOnboardingBannerDataLoaded,
		handleTitleChange,
		onClickOpenContentCreationPanel,
	} = props;

	const { locale } = useSessionData();
	const [isVisible, setIsVisible] = useState(false);
	const [isLandInEditorBannerShown, setIsLandInEditorBannerShown] = useState(false);
	const [showingOfflineBanner, setShowingOfflineBanner] = useState<boolean>(false);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		Object.values(EDITOR_ONBOARDING_STATE_KEYS),
		(version as number) !== 1 && (version as VersionType)?.number !== 1,
	);
	const deserializedData = deserializeState(onboardingStateData);
	const { editorOnboardingEligiblity, wasDraftBannerSeen, isLandInEditorBannerVisible } =
		deserializedData;

	// Only check the labels, if they are eligible for the tutorial experiment to begin with.
	const skip =
		!onboardingStateData ||
		onboardingStateLoading ||
		Boolean(onboardingStateError) ||
		/* Check to see if they are a new user */ !editorOnboardingEligiblity ||
		isLandInEditorBannerVisible; //Skip if user is eligible for Land In Editor Banner

	const { isTemplate, isOnboardingTemplate, labelsData, labelsLoading, labelsError } =
		useCheckIfOnboardingEditorTemplate({
			contentId,
			skip,
		});

	const { shouldIncludeEditorTutorialTemplate, hasQueryErrors } =
		useIncludeEditorTutorialTemplate();

	// Connection banner height has a delay to appear, adjust the draft banner height to reflect this delay
	useEffect(() => {
		const delayTimeout: ReturnType<typeof setTimeout> = setTimeout(
			() => setShowingOfflineBanner(isShowingOfflineBanner),
			DELAY_TIMEOUT,
		);

		return () => clearTimeout(delayTimeout);
	}, [isShowingOfflineBanner]);

	useEffect(() => {
		if (labelsData && !labelsLoading) {
			// the original onboarding template should not have the draft banner
			if (isOnboardingTemplate || !shouldIncludeEditorTutorialTemplate) {
				setIsOnboardingBannerDataLoaded(true);
				return;
			}

			if (!wasDraftBannerSeen) {
				setIsVisible(true);
			}

			// land in editor banner has no labelsData
		} else if (isLandInEditorBannerVisible) {
			setIsVisible(true);
			setIsLandInEditorBannerShown(true);
		}
	}, [
		isOnboardingTemplate,
		isTemplate,
		labelsData,
		labelsLoading,
		locale,
		shouldIncludeEditorTutorialTemplate,
		setIsOnboardingBannerDataLoaded,
		spaceKey,
		wasDraftBannerSeen,
		isLandInEditorBannerVisible,
	]);

	// note: unauthorized errors are handled in the queries themselves
	if (onboardingStateError) {
		return <ErrorDisplay error={onboardingStateError} />;
	} else if (labelsError) {
		return <ErrorDisplay error={labelsError} />;
	}

	if (!isVisible) {
		// TODO: Manage logic in a separate component https://hello.atlassian.net/browse/CONFONBO-1674
		// These are all conditions where we should not block the Editor Help Center from rendering
		const finishedLoading: boolean =
			// there are any errors in the queries
			onboardingStateError ||
			labelsError ||
			hasQueryErrors ||
			// the user is not eligible for the experiment
			(onboardingStateData && !editorOnboardingEligiblity) ||
			wasDraftBannerSeen;

		if (finishedLoading) {
			setIsOnboardingBannerDataLoaded(true);
		}
		return null;
	}

	const [BannerComponent, experienceName] = isLandInEditorBannerShown
		? [LandInEditorBanner, ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE]
		: [EditorDraftBanner, ONBOARDING_EDITOR_DRAFT_BANNER_EXPERIENCE];

	return (
		<>
			{isLandInEditorBannerShown && <ExperienceStart name={experienceName} />}
			<BannerComponent
				{...props}
				showingOfflineBanner={showingOfflineBanner}
				setIsVisible={setIsVisible}
				deserializedData={onboardingStateData && deserializedData}
				editorActions={editorActions}
				onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
				handleTitleChange={handleTitleChange}
			/>
		</>
	);
};

export const OnboardingEditorBanner = (props) => {
	return (
		<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ADOPTION}>
			<BaseOnboardingEditorBanner {...props} />
		</TransparentErrorBoundary>
	);
};
